import ReactDOM from 'react-dom';

import { openOwnWindow } from './own-window';

/**
 *  Z-Index for dialogs. Should be higher than other UI elements.
 *
 *  If positioned above other elements (usually floating elements),
 *  ensure the element in question is part for the dialog hierarchy,
 *  and not placed in the page body / root element.
 */
export const dialogZIndex = 10000;

/**
 * A movable dialog, see Props.movable in dialog.tsx, which is docked with the others.
 */
type DockedDialog = {
  id: string;
  /**
   * The element the dialog is rendered into, which is moved between groups and windows, so that
   * the dialog is not rendered anew, and keeps its state.
   */
  host: HTMLElement;
  /**
   * Closes the dialog, as its window is closed.
   */
  onClose: () => void;
};

/**
 * Dialogs docked together, stacked from the top down, and as wide as the widest of them. A dialog
 * that isn't docked to any other is a group of its own.
 */
type Group = {
  dialogs: DockedDialog[];
  /**
   * The element the dialogs are stacked in, in the page, or in the window of the group.
   */
  container: HTMLElement;
  position: Position;
  /**
   * The window of its own the group is in, see popOut.
   */
  window?: Window;
  /**
   * Where the group was in the page before it was put in its window, to put it back there.
   */
  pagePosition?: Position;
  /**
   * Whether the window is closed by the dock rather than by its close button, which closes the
   * dialogs in it.
   */
  closing?: boolean;
};

type Position = { left: number; top: number };

/**
 * Where a dialog is in the dock, for it to render accordingly.
 */
type DockState = {
  /**
   * The place of the dialog in its group, from the top.
   */
  index: number;
  /**
   * How many dialogs the group has.
   */
  count: number;
  window?: Window;
};

const undocked: DockState = { index: 0, count: 1 };

/**
 * How much of a moved dialog (pixels) stays within the window, so that its header can be dragged
 * back.
 */
const minVisibleWidth = 60;
const minVisibleHeight = 40;

/**
 * How close (pixels) to the edges of the window a dialog opens.
 */
const viewportEdge = 5;

/**
 * How far (pixels) the header of a dialog can move while it is pressed and still be clicked.
 */
const clickTolerance = 3;

/**
 * How close (pixels) the top of a dragged dialog is to the bottom of another to be docked below it.
 */
const dockDistance = 24;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getElement = (dialog: DockedDialog) =>
  dialog.host.querySelector<HTMLElement>('[role="dialog"]')!;

/**
 * Docks the movable dialogs together, and moves them, in the page and in windows of their own.
 *
 * - The dialogs that open beside the asset menu dock below each other, see add.
 * - Dragging the header of the top dialog of a group moves the group. Dragging the header of one
 *   below detaches it, with the dialogs below it. Dropping a dialog with its top at the bottom of
 *   another docks it, and those below it, below that one.
 * - A dialog is opened in a window of its own with the dialogs below it, which can be moved outside
 *   the browser window, e.g. to another screen, see openOwnWindow. There is one such window for
 *   the dialogs: another dialog is docked at its bottom.
 */
class DialogDock {
  private groups: Group[] = [];
  private listeners = new Set<() => void>();
  private states = new Map<string, DockState>();
  // The open dialogs that docked by themselves, the last of which the next one docks below, see add.
  private autoDocked: string[] = [];
  // Whether the header was dragged since it was pressed, rather than clicked, see wasDragged.
  private dragged = false;
  // The dialog that a dragged one is docked below if it is dropped, see drag.
  private dockTarget?: HTMLElement;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = (id: string) => this.states.get(id) ?? undocked;

  /**
   * Adds a dialog, which is rendered into its host. One that docks by itself, e.g. beside the asset
   * menu, is docked below the last one that did and is still open, wherever it is. Otherwise it is
   * a group of its own, at the position, moved within the window: in the middle of it, or at its
   * top left.
   */
  add(dialog: DockedDialog, options: { autoDock: boolean; position: 'center' | Position }) {
    const last = this.autoDocked.at(-1);
    const below = options.autoDock && last ? this.find(last) : undefined;
    if (options.autoDock) {
      this.autoDocked.push(dialog.id);
    }

    if (below) {
      below.group.dialogs.push(dialog);
      this.arrange(below.group);
      this.fitWindow(below.group);
      this.emit();
      return;
    }

    const group = this.createGroup([dialog], { left: 0, top: 0 });
    const { width, height } = group.container.getBoundingClientRect();
    const { left, top } =
      options.position === 'center'
        ? { left: (window.innerWidth - width) / 2, top: (window.innerHeight - height) / 2 }
        : options.position;
    this.moveTo(group, {
      left: clamp(left, viewportEdge, window.innerWidth - width - viewportEdge),
      top: clamp(top, viewportEdge, window.innerHeight - height - viewportEdge),
    });
    this.emit();
  }

  /**
   * Removes a dialog that is closed. The dialogs below it move up, and a window that is left empty
   * is closed.
   */
  remove(id: string) {
    const found = this.find(id);
    if (!found) {
      return;
    }

    const { group, index } = found;
    this.autoDocked = this.autoDocked.filter((x) => x !== id);
    group.dialogs.splice(index, 1);
    group.dialogs.length ? this.arrange(group) : this.removeGroup(group);
    this.fitWindow(group);
    this.emit();
  }

  /**
   * Whether the click on a header that ends a drag is one, which doesn't collapse the dialog.
   */
  wasDragged = () => this.dragged;

  /**
   * Moves the group of a dialog as its header is dragged, from a pointer down on it, until the
   * pointer is up. Its group is detached below the dialogs above it once it moves, and docked below
   * the dialog its top is dropped at the bottom of, which is marked, see Component in dialog.tsx.
   */
  drag(id: string, e: PointerEvent) {
    this.dragged = false;
    const view = e.view ?? window;
    let dragged: { group: Group; origin: Position } | undefined;

    const onMove = (move: PointerEvent) => {
      const x = move.clientX - e.clientX;
      const y = move.clientY - e.clientY;

      if (!dragged) {
        if (Math.hypot(x, y) <= clickTolerance) {
          return;
        }

        this.dragged = true;
        const group = this.detach(id);
        dragged = { group, origin: group.position };
      }

      const { group, origin } = dragged;
      const width = group.container.offsetWidth;
      this.moveTo(group, {
        left: clamp(origin.left + x, minVisibleWidth - width, view.innerWidth - minVisibleWidth),
        top: clamp(origin.top + y, 0, view.innerHeight - minVisibleHeight),
      });
      this.markDockTarget(this.findDockTarget(group));
    };

    const onUp = () => {
      view.removeEventListener('pointermove', onMove);
      view.removeEventListener('pointerup', onUp);
      view.removeEventListener('pointercancel', onUp);

      // After the click that ends the drag, which comes right after.
      setTimeout(() => (this.dragged = false));

      const target = this.dockTarget && this.findByElement(this.dockTarget);
      this.markDockTarget(undefined);
      if (dragged && target) {
        this.dock(dragged.group, target);
      }
    };

    view.addEventListener('pointermove', onMove);
    view.addEventListener('pointerup', onUp);
    view.addEventListener('pointercancel', onUp);
  }

  /**
   * Opens a dialog in a window of its own with the dialogs below it, as large as they are, and a
   * popup window where they are on the screen. Where a window is open already, they are docked at
   * its bottom. Must be called from a click, or the browser blocks the window, and the dialogs stay
   * in the page, docked where they were, as they do when the window is closed before it has loaded.
   */
  popOut(id: string) {
    const { group: above, index } = this.find(id)!;
    const group = this.detach(id);
    const windowGroup = this.groups.find((x) => x.window);

    if (windowGroup) {
      this.dock(group, { group: windowGroup, index: windowGroup.dialogs.length - 1 });
      return;
    }

    // Docked back where they were, unless they have been moved meanwhile, e.g. into a window.
    const stay = () => {
      if (
        group !== above &&
        !group.window &&
        [group, above].every((x) => this.groups.includes(x))
      ) {
        this.dock(group, { group: above, index: Math.min(index, above.dialogs.length) - 1 });
      }
    };
    const title =
      getElement(group.dialogs[0]).querySelector('[data-testid="dialog-header"]')?.textContent ||
      document.title;
    openOwnWindow(
      group.container.getBoundingClientRect(),
      title,
      (ownWindow) => {
        // Not where it was popped out again while the first window loaded.
        if (this.groups.includes(group) && !group.window) {
          this.moveToWindow(group, ownWindow);
        } else {
          ownWindow.close();
        }
      },
      { failed: stay, closed: stay },
    );
  }

  /**
   * Puts a dialog in a window back in the page, with the dialogs below it, where the window's
   * dialogs were before. The window is closed when it is left empty.
   */
  putBack(id: string) {
    const found = this.find(id);
    if (!found?.group.window) {
      return;
    }

    const { group, index } = found;
    const pagePosition = group.pagePosition ?? { left: 0, top: 0 };

    if (index > 0) {
      this.createGroup(group.dialogs.splice(index), pagePosition);
      this.fitWindow(group);
      this.emit();
      return;
    }

    group.closing = true;
    group.window!.close();
    this.moveToPage(group, pagePosition);
    this.emit();
  }

  /**
   * Fits the window of the group of a dialog to the dialogs in it, e.g. after one is collapsed or
   * expanded. The browser resizes it only during a click in it, or another user action.
   */
  fitWindowOf(id: string) {
    const found = this.find(id);
    if (found) {
      this.fitWindow(found.group);
    }
  }

  private fitWindow(group: Group) {
    const popup = group.window;
    if (!popup || !group.dialogs.length) {
      return;
    }

    // Its height without filling the window, as wide as the window, which keeps its width: the
    // widths of the dialogs depend on it.
    const { style } = group.container;
    style.bottom = 'auto';
    const height = group.container.offsetHeight;
    style.bottom = '0';

    try {
      popup.resizeBy(0, height - popup.innerHeight);
    } catch {
      // Not resized without a user action, when the dialogs in it scroll.
    }
  }

  private find(id: string) {
    for (const group of this.groups) {
      const index = group.dialogs.findIndex((x) => x.id === id);
      if (index >= 0) {
        return { group, index };
      }
    }
  }

  private findByElement(element: HTMLElement) {
    for (const group of this.groups) {
      const index = group.dialogs.findIndex((x) => getElement(x) === element);
      if (index >= 0) {
        return { group, index };
      }
    }
  }

  /**
   * Detaches a dialog and those below it from the dialogs above it, into a group of their own where
   * they are. The group of a dialog at the top is returned as it is.
   */
  private detach(id: string) {
    const { group, index } = this.find(id)!;
    if (index === 0) {
      return group;
    }

    const { left, top } = getElement(group.dialogs[index]).getBoundingClientRect();
    const detached = this.createGroup(group.dialogs.splice(index), { left, top });
    this.arrange(group);
    this.emit();
    return detached;
  }

  /**
   * Docks the dialogs of a group below a dialog of another, above the dialogs below that one.
   */
  private dock(group: Group, below: { group: Group; index: number }) {
    below.group.dialogs.splice(below.index + 1, 0, ...group.dialogs);
    group.dialogs = [];
    this.removeGroup(group);
    this.arrange(below.group);
    this.fitWindow(below.group);
    this.emit();
  }

  /**
   * The dialog of another group in the page whose bottom the top of a group is at, and which it
   * overlaps sideways.
   */
  private findDockTarget(group: Group) {
    const { left, top } = group.position;
    const right = left + group.container.getBoundingClientRect().width;
    let target: { element: HTMLElement; distance: number } | undefined;

    for (const other of this.groups) {
      if (other === group || other.window) {
        continue;
      }

      for (const dialog of other.dialogs) {
        const element = getElement(dialog);
        const rect = element.getBoundingClientRect();
        const distance = Math.abs(top - rect.bottom);

        if (
          distance <= dockDistance &&
          left < rect.right &&
          right > rect.left &&
          (!target || distance < target.distance)
        ) {
          target = { element, distance };
        }
      }
    }

    return target?.element;
  }

  private markDockTarget(element: HTMLElement | undefined) {
    if (element === this.dockTarget) {
      return;
    }

    this.dockTarget?.removeAttribute('data-dock-target');
    element?.setAttribute('data-dock-target', '');
    this.dockTarget = element;
  }

  private createGroup(dialogs: DockedDialog[], position: Position) {
    const container = document.createElement('div');
    // Brought in front of the other dialogs, see bringToFront in dialog.tsx.
    container.setAttribute('data-dialog-wrapper', '');
    container.setAttribute('data-dialog-group', '');
    document.body.append(container);

    const group: Group = { dialogs, container, position };
    this.groups.push(group);
    this.applyStyle(group);
    this.arrange(group);
    this.moveTo(group, position);
    return group;
  }

  private removeGroup(group: Group) {
    this.groups = this.groups.filter((x) => x !== group);
    group.container.remove();

    if (group.window) {
      group.closing = true;
      group.window.close();
    }
  }

  /**
   * Puts the hosts of the dialogs of a group in its container, in their order.
   */
  private arrange(group: Group) {
    group.dialogs.forEach(({ host }, i) => {
      // Moved only when out of place, which would take the focus out of it.
      if (group.container.children[i] !== host) {
        group.container.insertBefore(host, group.container.children[i] ?? null);
      }
    });
  }

  private moveTo(group: Group, position: Position) {
    group.position = position;
    if (!group.window) {
      group.container.style.left = `${position.left}px`;
      group.container.style.top = `${position.top}px`;
    }
  }

  private moveToWindow(group: Group, popup: Window) {
    group.pagePosition = group.position;
    group.window = popup;
    group.closing = false;
    popup.document.body.append(group.container);
    this.applyStyle(group);

    const closeWithPage = () => {
      group.closing = true;
      popup.close();
    };
    window.addEventListener('pagehide', closeWithPage);
    popup.addEventListener(
      'pagehide',
      () => {
        window.removeEventListener('pagehide', closeWithPage);
        if (group.window !== popup || group.closing) {
          return;
        }

        // Its close button closes the dialogs, and those that stay open are put back in the page.
        ReactDOM.flushSync(() => group.dialogs.forEach((x) => x.onClose()));
        if (this.groups.includes(group)) {
          this.moveToPage(group, group.pagePosition!);
        }
        this.emit();
      },
      { once: true },
    );

    this.emit();
  }

  private moveToPage(group: Group, position: Position) {
    group.window = undefined;
    document.body.append(group.container);
    this.applyStyle(group);
    this.moveTo(group, position);
  }

  private applyStyle(group: Group) {
    Object.assign(
      group.container.style,
      group.window
        ? {
            position: 'fixed',
            left: '0',
            top: '0',
            right: '0',
            bottom: '0',
            width: 'auto',
            maxHeight: 'none',
            borderRadius: '0',
            boxShadow: 'none',
            // Where the window is higher than the dialogs, see Header in dialog.tsx.
            backgroundColor: '#eee',
          }
        : {
            // Placed by its left and top, see moveTo.
            position: 'fixed',
            right: 'auto',
            bottom: 'auto',
            width: 'max-content',
            maxHeight: `calc(100vh - ${2 * viewportEdge}px)`,
            borderRadius: '10px',
            boxShadow: '2px 2px 8px 0 #00000060',
            backgroundColor: '',
          },
      {
        zIndex: `${dialogZIndex}`,
        isolation: 'isolate',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      },
    );
  }

  private emit() {
    this.states = new Map(
      this.groups.flatMap((group) =>
        group.dialogs.map((dialog, index) => {
          const previous = this.states.get(dialog.id);
          const state = { index, count: group.dialogs.length, window: group.window };
          return [
            dialog.id,
            previous &&
            previous.index === state.index &&
            previous.count === state.count &&
            previous.window === state.window
              ? previous
              : state,
          ] as const;
        }),
      ),
    );
    this.listeners.forEach((listener) => listener());
  }
}

export const dialogDock = new DialogDock();
